// The authenticated session + active account (Better Auth cookie session, D17;
// POST /v1/session/active-account, contracts.md:52). A discriminated union
// (app-engineering §2) so a screen can never read `account`/`membership` before both
// are actually resolved - the type system forces the loading/signed-out/error branches
// to be handled first.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { Account, Membership } from "@nightcrew/shared";
import { getSession, type SessionUser } from "../api/auth.js";
import { selectActiveAccount } from "../api/session.js";

export type SessionState =
  | { readonly status: "loading" }
  | { readonly status: "signed_out" }
  | { readonly status: "error"; readonly message: string }
  | {
      readonly status: "ready";
      readonly user: SessionUser;
      readonly account: Account;
      readonly membership: Membership;
    };

interface SessionContextValue {
  readonly state: SessionState;
  readonly refresh: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const SessionProvider = ({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element => {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const cancelledRef = { current: false };
    // A function call (not a direct property read) so TypeScript's control-flow
    // analysis never caches the result across the `await` points below - two direct
    // `cancelledRef.current` reads in the same async body narrow the SECOND read to a
    // stale "always false" (a real no-unnecessary-condition finding, since nothing
    // reassigns it in the visible synchronous flow), even though the cleanup closure
    // can genuinely flip it between awaits.
    const isCancelled = (): boolean => cancelledRef.current;
    setState({ status: "loading" });

    void (async () => {
      const sessionResult = await getSession();
      if (isCancelled()) return;
      if (isErr(sessionResult)) {
        setState({ status: "error", message: "We could not check your sign-in. Try again." });
        return;
      }
      if (sessionResult.value === null) {
        setState({ status: "signed_out" });
        return;
      }
      const accountResult = await selectActiveAccount();
      if (isCancelled()) return;
      if (isErr(accountResult)) {
        setState({ status: "error", message: "We could not load your account. Try again." });
        return;
      }
      setState({
        status: "ready",
        user: sessionResult.value.user,
        account: accountResult.value.account,
        membership: accountResult.value.membership,
      });
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [generation]);

  const value = useMemo(
    () => ({
      state,
      refresh: () => {
        setGeneration((g) => g + 1);
      },
    }),
    [state],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextValue => {
  const ctx = useContext(SessionContext);
  if (ctx === null) throw new Error("useSession must be used inside a SessionProvider");
  return ctx;
};

/** True for a viewer membership - the UI convenience hide only; the server is the real
 * gate (slice-2-onboarding.md §2.3, ADR-0016 §4). */
export const isViewerRole = (state: SessionState): boolean =>
  state.status === "ready" && state.membership.role === "viewer";

export const isOwnerOrAdmin = (state: SessionState): boolean =>
  state.status === "ready" &&
  (state.membership.role === "owner" || state.membership.role === "admin");

/** Gate for every authed route: loading skeleton, redirect to sign-in, or a retryable
 * error - never a silent blank screen. Deliberately does NOT wrap children in the full
 * app shell (sidebar + topbar) - the onboarding screens (1-6) use the focused-flow shell
 * instead (slice-2-onboarding.md §2.2); `AppShellLayout` below is for the shell-based
 * routes (portfolio, connections). */
export const RequireSession = ({
  children,
}: {
  readonly children: ReactNode;
}): React.JSX.Element => {
  const { state, refresh } = useSession();
  const location = useLocation();

  if (state.status === "loading") {
    return (
      <div style={{ padding: 28 }}>
        <div className="skeleton skeleton-line" style={{ width: "40%" }} />
        <div className="skeleton skeleton-card mt16" />
      </div>
    );
  }
  if (state.status === "signed_out") {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />;
  }
  if (state.status === "error") {
    return (
      <div className="state-card card pad" style={{ maxWidth: 480, margin: "60px auto" }}>
        <p>{state.message}</p>
        <div className="actions">
          <button type="button" className="btn btn-primary" onClick={refresh}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};
